import React from 'react';
import { Box, Typography, Divider } from '@mui/material';

/**
 * Compact markdown renderer for print view
 * Removes redundant titles and makes layout more compact
 */
const CompactMarkdownRenderer = ({ markdown }) => {
    if (!markdown) return null;

    // Filter out AI generation disclaimer text
    let filteredMarkdown = markdown;
    const disclaimerPatterns = [
        /^\*?This CV was generated using AI\. Please review and customize as needed\.\*?$/i,
        /^\*?This CV was generated using AI\.\*?$/i,
        /^\*?Generated using AI\. Please review and customize as needed\.\*?$/i,
    ];
    
    const lines = markdown.split('\n');
    const filteredLines = lines.filter(line => {
        const trimmed = line.trim();
        return !disclaimerPatterns.some(pattern => pattern.test(trimmed));
    });
    
    filteredMarkdown = filteredLines.join('\n');
    const filteredLinesArray = filteredMarkdown.split('\n');

    const elements = [];
    let currentParagraph = [];
    let inList = false;
    let listItems = [];
    let isFirstHeading = true;
    let contactInfo = [];
    let hasProcessedHeader = false;

    const renderParagraph = () => {
        if (currentParagraph.length > 0) {
            const text = currentParagraph.join(' ').trim();
            if (text) {
                elements.push(
                    <Typography key={`p-${elements.length}`} variant="body2" paragraph sx={{ mb: 1, '@media print': { fontSize: '11px', mb: 0.5 } }}>
                        {parseInlineFormatting(text)}
                    </Typography>
                );
            }
            currentParagraph = [];
        }
    };

    const renderList = () => {
        if (listItems.length > 0) {
            elements.push(
                <Box key={`list-${elements.length}`} component="ul" sx={{ pl: 2, mb: 1.5, '@media print': { mb: 1 } }}>
                    {listItems.map((item, idx) => (
                        <Typography key={idx} component="li" variant="body2" sx={{ mb: 0.3, '@media print': { fontSize: '11px', mb: 0.2 } }}>
                            {parseInlineFormatting(item)}
                        </Typography>
                    ))}
                </Box>
            );
            listItems = [];
        }
    };

    const parseInlineFormatting = (text) => {
        const parts = [];
        let currentIndex = 0;

        // Handle bold **text**
        const boldRegex = /\*\*([^*]+)\*\*/g;
        let match;
        let lastIndex = 0;

        while ((match = boldRegex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index));
            }
            parts.push(
                <Typography key={`bold-${currentIndex}`} component="span" fontWeight={600}>
                    {match[1]}
                </Typography>
            );
            lastIndex = match.index + match[0].length;
            currentIndex++;
        }

        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }

        return parts.length > 0 ? parts : text;
    };

    // First pass: Extract name and contact info
    let name = '';
    let contactSectionStartIndex = -1;
    let contactSectionEndIndex = -1;
    
    filteredLinesArray.forEach((line, index) => {
        const trimmed = line.trim();
        
        // Extract name from first H1 (skip "AI Generated CV" type titles)
        if (trimmed.startsWith('# ') && isFirstHeading) {
            const nameText = trimmed.substring(2).trim();
            // Skip generic titles like "AI Generated CV"
            if (!nameText.toLowerCase().includes('generated') && !nameText.toLowerCase().includes('cv')) {
                name = nameText;
                hasProcessedHeader = true;
            }
            isFirstHeading = false;
        }
        
        // Find Contact Information section
        if (trimmed.toLowerCase() === '## contact information' || trimmed.toLowerCase() === '## contact') {
            contactSectionStartIndex = index;
            hasProcessedHeader = true;
            // Collect contact info from next lines
            let i = index + 1;
            while (i < filteredLinesArray.length && i < index + 15) {
                const nextLine = filteredLinesArray[i].trim();
                if (nextLine.startsWith('##')) {
                    contactSectionEndIndex = i;
                    break; // Next section
                }
                if (nextLine === '') {
                    i++;
                    continue;
                }
                
                // Handle different formats: "Email: value", "- Email: value", "**Email:** value"
                const cleanLine = nextLine.replace(/^[-*]\s*/, '').replace(/\*\*/g, '').trim();
                
                const emailMatch = cleanLine.match(/email[:\s]+(.+)/i);
                if (emailMatch && !contactInfo.find(c => c.type === 'email')) {
                    contactInfo.push({ type: 'email', value: emailMatch[1].trim() });
                }
                
                const phoneMatch = cleanLine.match(/phone[:\s]+(.+)/i);
                if (phoneMatch && !contactInfo.find(c => c.type === 'phone')) {
                    contactInfo.push({ type: 'phone', value: phoneMatch[1].trim() });
                }
                
                const locationMatch = cleanLine.match(/location[:\s]+(.+)/i);
                if (locationMatch && !contactInfo.find(c => c.type === 'location')) {
                    contactInfo.push({ type: 'location', value: locationMatch[1].trim() });
                }
                
                i++;
            }
            if (contactSectionEndIndex === -1) {
                contactSectionEndIndex = i;
            }
        }
    });
    
    // Second pass: Render content (skip contact section and professional summary title)
    filteredLinesArray.forEach((line, index) => {
        const trimmed = line.trim();

        // Skip first H1 if it's the name (already extracted)
        if (trimmed.startsWith('# ')) {
            const nameText = trimmed.substring(2).trim();
            if (nameText.toLowerCase().includes('generated') || nameText.toLowerCase().includes('cv')) {
                return; // Skip generic titles
            }
            if (name && nameText === name) {
                return; // Skip name H1, we'll render it in header
            }
        }

        // Skip Contact Information section entirely
        if (contactSectionStartIndex !== -1 && index >= contactSectionStartIndex && index < contactSectionEndIndex) {
            return; // Skip contact section header and all its content
        }

        // Professional Summary - skip title, just show content
        if (trimmed.toLowerCase() === '## professional summary' || trimmed.toLowerCase() === '## summary') {
            // Skip the title, just continue to next line
            return;
        }

        // Other headings
        if (trimmed.startsWith('## ')) {
            renderParagraph();
            renderList();
            inList = false;
            elements.push(
                <Typography key={`h2-${index}`} variant="h6" fontWeight={600} gutterBottom sx={{ mt: 2, mb: 1, '@media print': { fontSize: '14px', mt: 1.5, mb: 0.5 } }}>
                    {trimmed.substring(3)}
                </Typography>
            );
        } else if (trimmed.startsWith('### ')) {
            renderParagraph();
            renderList();
            inList = false;
            elements.push(
                <Typography key={`h3-${index}`} variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 1.5, mb: 0.5, '@media print': { fontSize: '12px', mt: 1, mb: 0.3 } }}>
                    {trimmed.substring(4)}
                </Typography>
            );
        } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            renderParagraph();
            if (!inList) {
                inList = true;
            }
            listItems.push(trimmed.substring(2));
        } else if (trimmed === '---' || trimmed === '***') {
            renderParagraph();
            renderList();
            inList = false;
            elements.push(<Divider key={`divider-${index}`} sx={{ my: 1.5, '@media print': { my: 1 } }} />);
        } else if (trimmed === '') {
            renderParagraph();
            renderList();
            inList = false;
        } else {
            if (inList) {
                renderList();
                inList = false;
            }
            currentParagraph.push(trimmed);
        }
    });

    renderParagraph();
    renderList();

    return (
        <Box>
            {/* Compact header with name and contact info in one line */}
            {(name || contactInfo.length > 0) && (
                <Box sx={{ mb: 1.5, '@media print': { mb: 1 } }}>
                    {/* Name */}
                    {name && (
                        <Typography 
                            variant="h4" 
                            fontWeight={700} 
                            sx={{ 
                                mb: 0.3,
                                '@media print': { 
                                    fontSize: '18px', 
                                    mb: 0.2,
                                    lineHeight: 1.2
                                } 
                            }}
                        >
                            {name}
                        </Typography>
                    )}
                    {/* Contact info in one line with icons */}
                    {contactInfo.length > 0 && (
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: 1.5,
                                alignItems: 'center',
                                '@media print': {
                                    gap: 1,
                                    fontSize: '9px',
                                    lineHeight: 1.3
                                }
                            }}
                        >
                            {contactInfo.map((info, idx) => (
                                <Typography 
                                    key={idx} 
                                    variant="body2" 
                                    component="span"
                                    sx={{ 
                                        fontSize: '12px',
                                        '@media print': { 
                                            fontSize: '9px',
                                        } 
                                    }}
                                >
                                    {info.type === 'email' && '📧 '}
                                    {info.type === 'phone' && '📞 '}
                                    {info.type === 'location' && '📍 '}
                                    {info.value}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
            {/* Rest of the content */}
            <Box sx={{ '& p': { mb: 1 }, '& ul': { mb: 1.5 }, '@media print': { '& p': { mb: 0.5, fontSize: '11px' }, '& ul': { mb: 1, fontSize: '11px' } } }}>
                {elements}
            </Box>
        </Box>
    );
};

export default CompactMarkdownRenderer;

